import { Category } from '@/constants/categories';

interface CategoryItemProps {
  category: Category;
  index: number;
  isVisible: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

function CategoryItem({
  category,
  index,
  isVisible,
  isExpanded,
  onToggle,
  children,
}: CategoryItemProps) {
  const isExpandable = category.isExpandable || false;

  return (
    <div
      className={`py-6 transition-all duration-500 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[-20px] opacity-0'
      }`}
      style={{
        transitionDelay: `${300 + index * 50}ms`,
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className={`size-16 ${
            category.iconBg || 'bg-unleash-pink-light'
          } flex shrink-0 items-center justify-center rounded-full transition-transform duration-300 hover:scale-110`}
        >
          {category.icon ? (
            <category.icon
              size={32}
              strokeWidth={2}
              className={category.iconText || 'text-gray-600'}
            />
          ) : (
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- labelled emoji, not an image file
            <span className="text-3xl" role="img" aria-label={category.name}>
              {category.emoji}
            </span>
          )}
        </div>
        <div className="flex-1">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">{category.name}</h2>
            {isExpandable && (
              <button
                onClick={onToggle}
                className="ml-4 flex size-8 items-center justify-center rounded-full bg-red-100 text-red-600 transition-all duration-200 hover:scale-110 hover:bg-red-200 hover:text-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-hidden"
                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
              >
                <svg className="size-5" fill="currentColor" viewBox="0 0 20 20">
                  {isExpanded ? (
                    <path
                      fillRule="evenodd"
                      d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  )}
                </svg>
              </button>
            )}
          </div>
          <p className="text-gray-700">{category.description}</p>

          {isExpandable && (
            <div
              className={`mt-6 overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { CategoryItem };
